import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { api } from '../utils/api';
import { QRCodeSVG } from 'qrcode.react';
import { useLanguage } from '../utils/i18n';
import { validateField } from '../utils/validation';
import { useNotification } from '../utils/NotificationContext';
import LanguageToggle from '../components/LanguageToggle';
import Logo from '../components/Logo';
import { ArrowLeft } from 'lucide-react';


const FRONTEND_URL = process.env.REACT_APP_FRONTEND_URL || window.location.origin;
const BACKEND_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000';

export default function ClientRegister() {
  const { businessId } = useParams();
  const { t } = useLanguage();
  const { showNotification } = useNotification();
  const [business, setBusiness] = useState(null);
  const [loadingBiz, setLoadingBiz] = useState(true);

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');

  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [error, setError] = useState('');
  const [successData, setSuccessData] = useState(null);
  const [design, setDesign] = useState(null);

  useEffect(() => {
    async function loadData() {
      try {
        const [bizData, designData] = await Promise.all([
          api.getBusinessPublic(businessId),
          api.getDesign(businessId).catch(() => null)
        ]);
        setBusiness(bizData);
        setDesign(designData);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoadingBiz(false);
      }
    }
    if (businessId) loadData();
  }, [businessId]);

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    setLoadingSubmit(true);

    // Regex Validations
    if (!validateField('name', name, true)) {
      setError(t('err_valid_name'));
      setLoadingSubmit(false);
      return;
    }
    if (email && !validateField('email', email)) {
      setError(t('err_valid_email'));
      setLoadingSubmit(false);
      return;
    }
    if (phone && !validateField('phone', phone)) {
      setError(t('err_valid_phone'));
      setLoadingSubmit(false);
      return;
    }

    try {
      // Detect browser language
      const userLang = (navigator.language || 'en').split('-')[0];

      const data = await api.createClientCard({
        businessId,
        clientName: name,
        clientEmail: email,
        clientPhone: phone,
        lang: userLang
      });
      setSuccessData(data);
      showNotification(t('notify_welcome_ready'), 'success');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingSubmit(false);
    }
  }

  if (loadingBiz) return (
    <div style={{ textAlign: 'center', paddingTop: 100, color: 'var(--text-muted)' }}>
      {t('loading')}
    </div>
  );

  if (!business) return (
    <div style={{ textAlign: 'center', paddingTop: 100, color: 'var(--text-muted)', padding: '0 20px' }}>
      {error ? (
        <div className="alert alert-error" style={{ maxWidth: 400, margin: '0 auto' }}>{error}</div>
      ) : (
        t('business_not_found')
      )}
    </div>
  );

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', background: 'var(--bg)', position: 'relative' }}>
      {/* Professional Header */}
      <header className="header-container" style={{ position: 'relative', zIndex: 10 }}>
        <div style={{ width: 44 }}>{/* Spacer for symmetry */}</div>

        <div className="header-centered-logo">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Logo size={24} />
            <span style={{ fontWeight: 800, fontSize: 18, color: 'var(--text)', letterSpacing: -0.5 }}>Wafiyo</span>
          </div>
        </div>

        <LanguageToggle />
      </header>

      <div className="bg-shapes hide-mobile">
        <div className="shape shape-1" style={{ top: '-10%', left: '-10%', width: 600, height: 600, opacity: 0.15 }}></div>
        <div className="shape shape-2" style={{ bottom: '-10%', right: '-10%', width: 800, height: 800, opacity: 0.1 }}></div>
      </div>

      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px 20px', position: 'relative', zIndex: 1 }}>
        <div className="container" style={{ maxWidth: 460 }}>
          {!successData ? (
            <div className="glass-card hover-lift">
              <div style={{ textAlign: 'center', marginBottom: 24 }}>
                <div style={{ fontSize: 48, marginBottom: 12 }}>✨</div>
                <h1 style={{ fontSize: 24, fontWeight: 800, color: 'var(--text)' }}>{t('join_loyalty')}</h1>
              </div>

              {error && <div className="alert alert-error">{error}</div>}

              <form onSubmit={handleSubmit} noValidate>
                <div className="form-group">
                  <label>{t('full_name')} *</label>
                  <input value={name} onChange={e => setName(e.target.value)} required placeholder="John Doe" />
                </div>
                <div className="form-group">
                  <label>{t('email')} <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>{t('optional')}</span></label>
                  <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="john@example.com" />
                </div>
                <div className="form-group" style={{ marginBottom: 32 }}>
                  <label>{t('phone')} <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>{t('optional')}</span></label>
                  <input value={phone} onChange={e => setPhone(e.target.value)} placeholder="+1 555 0100" />
                </div>

                <button className="btn btn-primary btn-glow" type="submit" disabled={loadingSubmit} style={{ width: '100%', padding: '14px', fontSize: 16, justifyContent: 'center' }}>
                  {loadingSubmit ? t('creating') : t('create_card')}
                </button>
              </form>
            </div>
          ) : (
            <div className="glass-card hover-lift" style={{ textAlign: 'center', paddingBottom: 40 }}>
              <div style={{ fontSize: 48, marginBottom: 16 }}>🎉</div>
              <h2 style={{ fontSize: 28, fontWeight: 800, color: 'var(--text)', marginBottom: 8 }}>{t('youre_in')}</h2>
              <p style={{ color: 'var(--text-muted)', fontSize: 15, marginBottom: 32 }}>
                {t('loyalty_card_ready', { businessName: business.name })}
              </p>

              <div style={{ background: '#fff', padding: 24, borderRadius: 24, display: 'inline-block', marginBottom: 24, boxShadow: '0 10px 25px -5px rgba(0,0,0,0.5)' }}>
                <QRCodeSVG
                  value={`${FRONTEND_URL}/scan/${successData.card.id}`}
                  size={220}
                  level="H"
                  includeMargin={false}
                />
              </div>

              <p style={{ fontSize: 16, fontWeight: 700, color: 'var(--text)', marginBottom: 32 }}>
                {t('show_qr_every_visit')}
              </p>

              <div style={{ marginTop: 16, paddingTop: 24, borderTop: '1px solid rgba(255,255,255,0.05)' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12, alignItems: 'center' }}>
                  {successData.card.wallet_link && (
                    <a
                      href={successData.card.wallet_link}
                      target="_blank"
                      rel="noreferrer"
                      className="hover-lift"
                      style={{
                        textDecoration: 'none',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: '#ffffff',
                        color: '#3c4043',
                        padding: '12px 24px',
                        borderRadius: '12px',
                        border: '1px solid #dadce0',
                        fontSize: 15,
                        fontWeight: 600,
                        gap: 12,
                        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                        transition: 'all 0.2s ease',
                        width: '100%',
                        maxWidth: 280
                      }}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24"><path fill="#4285F4" d="M21.2 8.9l-1.7-3a.5.5 0 0 0-.7-.2l-1.7 1a.5.5 0 0 1-.7-.2L14.7 3.5a.5.5 0 0 0-.7-.2L12.3 4.3a.5.5 0 0 1-.7-.2L10.9 2.4a.5.5 0 0 0-.4-.3.5.5 0 0 0-.4.1L2.8 8.3a.5.5 0 0 0-.1.7l1.7 3a.5.5 0 0 0 .7.2L12 8l4 6.9" /><path fill="#EA4335" d="M16 14.9l-4-6.9-6.9 4a.5.5 0 0 1-.7-.2L2.8 9a.5.5 0 0 0-.1.7l5.1 8.8a.5.5 0 0 0 .7.2l1.7-1a.5.5 0 0 1 .7.2l1.7 3a.5.5 0 0 0 .7.2l1.7-1a.5.5 0 0 1 .7.2l.7 1.2a.5.5 0 0 0 .7.2l1.7-1a.5.5 0 0 0 .2-.7L16 14.9z" /></svg>
                      <span>{t('add_to_wallet')}</span>
                    </a>
                  )}

                  <a
                    href={`${BACKEND_URL}/apple-pass/${successData.card.wallet_object_id}`}
                    target="_blank"
                    rel="noreferrer"
                    className="hover-lift"
                    style={{
                      textDecoration: 'none',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: '#0a0a0a',
                      color: '#ffffff',
                      padding: '12px 24px',
                      borderRadius: '12px',
                      border: '1px solid rgba(255,255,255,0.1)',
                      fontSize: 15,
                      fontWeight: 600,
                      gap: 12,
                      boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                      transition: 'all 0.2s ease',
                      width: '100%',
                      maxWidth: 280
                    }}
                  >
                    <svg viewBox="0 0 384 512" width="20" height="20" fill="currentColor">
                      <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z" />
                    </svg>
                    <span>{t('add_to_apple_wallet')}</span>
                  </a>
                </div>
                <p style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 20, fontWeight: 500 }}>
                  {t('add_to_wallet_desc')}
                </p>
              </div>
            </div>
          )}
        </div>
        <style>{`
        @media (max-width: 480px) {
          .brand-name {
            display: none;
          }
        }
      `}</style>
      </div>
    </div>
  );
}
